import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ScrollText,
  Shield,
  Loader2,
  AlertCircle,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import api from '../lib/api';
import type { AuditLog, PaginatedResponse } from '../types';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import SecurityReportTab from '../components/security/SecurityReportTab';

type TabId = 'audit-log' | 'security-report';

export default function LogsPage() {
  const [activeTab, setActiveTab] = useState<TabId>('audit-log');
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const limit = 25;

  const { data, isLoading, error } = useQuery<PaginatedResponse<AuditLog>>({
    queryKey: ['logs', page, limit],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<AuditLog>>('/logs', {
        params: { page, limit, sort: 'createdAt:desc' },
      });
      return res.data;
    },
  });

  const filteredLogs = (data?.data ?? []).filter((log) => {
    const matchesSearch =
      log.user.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.entityType.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesAction = !actionFilter || log.action === actionFilter;
    const matchesEntity = !entityFilter || log.entityType === entityFilter;

    return matchesSearch && matchesAction && matchesEntity;
  });

  // Extract unique actions and entity types for filters
  const allActions = [...new Set(data?.data.map((l) => l.action) ?? [])];
  const allEntityTypes = [...new Set(data?.data.map((l) => l.entityType) ?? [])];

  const formatTimestamp = (dateStr: string): string => {
    return format(new Date(dateStr), 'dd.MM.yyyy HH:mm:ss', { locale: de });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {activeTab === 'audit-log' ? (
          <ScrollText size={28} className="text-primary" />
        ) : (
          <Shield size={28} className="text-primary" />
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {activeTab === 'audit-log' ? 'Audit Log' : 'Sicherheit'}
          </h1>
          <p className="text-sm text-gray-500">
            {activeTab === 'audit-log'
              ? `${data?.total ?? 0} Einträge gesamt`
              : 'Penetration-Tests & Sicherheitsanalyse'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('audit-log')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'audit-log'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <ScrollText size={18} />
          Audit Log
        </button>
        <button
          onClick={() => setActiveTab('security-report')}
          className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'security-report'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Shield size={18} />
          Sicherheitsbericht
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'audit-log' ? (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Durchsuchen..."
                className="input-field pl-10"
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
              />
            </div>

            <div className="relative">
              <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="input-field pl-10"
                value={actionFilter}
                onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              >
                <option value="">Alle Aktionen</option>
                {allActions.map((a) => (
                  <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <div className="relative">
              <Filter size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <select
                className="input-field pl-10"
                value={entityFilter}
                onChange={(e) => { setEntityFilter(e.target.value); setPage(1); }}
              >
                <option value="">Alle Typen</option>
                {allEntityTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={18} className="shrink-0" />
              <span>Audit-Logs konnten nicht geladen werden</span>
            </div>
          )}

          {/* Table */}
          <div className="card overflow-hidden p-0">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 size={24} className="animate-spin text-gray-400" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="py-12 text-center">
                <ScrollText size={48} className="mx-auto text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">Keine Log-Einträge gefunden</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-4 py-3">Zeitpunkt</th>
                      <th className="px-4 py-3">Benutzer</th>
                      <th className="px-4 py-3">Aktion</th>
                      <th className="px-4 py-3">Typ</th>
                      <th className="px-4 py-3">Objekt</th>
                      <th className="px-4 py-3">IP</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500 font-mono text-xs">
                          {formatTimestamp(log.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-full bg-primary-100 flex items-center justify-center">
                              <span className="text-xs font-medium text-primary">
                                {log.user.displayName.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">
                              {log.user.displayName}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="capitalize text-gray-700">
                            {log.action.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="badge-info">{log.entityType}</span>
                        </td>
                        <td className="px-4 py-3">
                          {(log as any).entityName ? (
                            <span className="text-sm font-medium text-gray-900">{(log as any).entityName}</span>
                          ) : (
                            <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded text-gray-600">
                              {log.entityId.slice(0, 8)}...
                            </code>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs font-mono">
                          {log.ipAddress ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Seite {page} von {data.totalPages} ({data.total} Einträge)
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  className="btn-secondary px-3 py-1.5"
                >
                  <ChevronLeft size={16} />
                </button>
                {Array.from({ length: Math.min(data.totalPages, 5) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(page - 2, data.totalPages - 4)) + i;
                  if (pageNum > data.totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                        pageNum === page
                          ? 'bg-primary text-white'
                          : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(data.totalPages, page + 1))}
                  disabled={page >= data.totalPages}
                  className="btn-secondary px-3 py-1.5"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <SecurityReportTab />
      )}
    </div>
  );
}
